import type { EventPayload } from '@side/analytics';

export enum TrackInteractionsEvent {
  ClickEditPhotoListItemButton,
  ClickEditPhotoDoneButton,
  UploadInteractionPhoto,
  LoadMoreTransactions,
  ClickAddTransaction,
  ClickNameBubbleClick,
  ClickCommunicateWithContact,
  ClickArchiveUnarchiveTransaction,
  ClickArchivedTransactionsButton,
  ClickSortButton,
  ClickSortValue,
  ClickCloneTransactionButton,
  ClickFiltersButton,
  ClickApplyFilters,
  ClickClearAllFilters,
  ClickChangeTransactionStatus,
  ClickClearSearchButton,
  BlurFromSearchField,
  ClickTransaction,
  CloneTransactionConfirmation,
  CloneModalMount,
  CloneModalUnmount,
  LeaseRepresentationNavigate,
  LeaseTransactionConfirmation,
  CloneModalDisplay,
  LeaseAddressNavigate,
  CreateLeaseErrorDisplay,
  LeaseWorkflowLanding,
  LeaseRepresentationSelect,
  ArchivedListView,
  BlurFilterSearchField,
  ClickGlobalToggle,
  ClickActionsMenu,
  ClearFilterAutocomplete,
  ToggleFilterOption,
  CloseFiltersDialog,
  ClickStatusToggleButton,
  ClickCancelCreateTransaction,
  ClickCreateTransaction,
  SelectField,
  EnterAddressDecouplingModal,
  ClickMLSAutofill,
  ClickAddressAutofill,
  DatePickerAction,
  ReadOnlyIndicatorMount,
  ReadOnlyUnlockButtonClick,
  UnlockReasonModalMount,
  UnlockReasonSelect,
  UnlockReasonOtherBlur,
  UnlockReasonModalConfirm,
  UnlockReasonModalDismiss,
  UnlockedBannerMount,
  ReLockButtonClick,
  ChangePage,
  ChangeRowsPerPage,
  ReadOnlyStatusTooltipReveal,
  DisabledStatusMenuItemTooltipReveal,
}

type EventTypeLabels = Record<TrackInteractionsEvent, string>;

export const AnalyticsEventLabels: EventTypeLabels = {
  [TrackInteractionsEvent.ClickEditPhotoListItemButton]: 'LIST_VIEW',
  [TrackInteractionsEvent.ClickEditPhotoDoneButton]: 'LIST_VIEW',
  [TrackInteractionsEvent.UploadInteractionPhoto]: 'LIST_VIEW',
  [TrackInteractionsEvent.ClickAddTransaction]: 'LIST_VIEW',
  [TrackInteractionsEvent.ClickNameBubbleClick]: 'LIST_VIEW',
  [TrackInteractionsEvent.LoadMoreTransactions]: 'LIST_VIEW',
  [TrackInteractionsEvent.ChangePage]: 'LIST_VIEW',
  [TrackInteractionsEvent.ChangeRowsPerPage]: 'LIST_VIEW',
  [TrackInteractionsEvent.ClickCommunicateWithContact]: 'LIST_VIEW',
  [TrackInteractionsEvent.ClickArchiveUnarchiveTransaction]: 'LIST_VIEW',
  [TrackInteractionsEvent.ClickArchivedTransactionsButton]: 'LIST_VIEW',
  [TrackInteractionsEvent.ClickSortButton]: 'LIST_VIEW',
  [TrackInteractionsEvent.ClickSortValue]: 'LIST_VIEW',
  [TrackInteractionsEvent.ClickCloneTransactionButton]: 'LIST_VIEW',
  [TrackInteractionsEvent.ClickFiltersButton]: 'LIST_VIEW',
  [TrackInteractionsEvent.ClickApplyFilters]: 'LIST_VIEW',
  [TrackInteractionsEvent.ClickClearAllFilters]: 'LIST_VIEW',
  [TrackInteractionsEvent.ClickChangeTransactionStatus]: 'LIST_VIEW',
  [TrackInteractionsEvent.ClickClearSearchButton]: 'LIST_VIEW',
  [TrackInteractionsEvent.BlurFromSearchField]: 'LIST_VIEW',
  [TrackInteractionsEvent.ClickTransaction]: 'LIST_VIEW',
  [TrackInteractionsEvent.CloneTransactionConfirmation]: 'LIST_VIEW',
  [TrackInteractionsEvent.CloneModalMount]: 'LIST_VIEW',
  [TrackInteractionsEvent.CloneModalUnmount]: 'LIST_VIEW',
  [TrackInteractionsEvent.CloneModalDisplay]: 'LIST_VIEW',
  [TrackInteractionsEvent.LeaseRepresentationNavigate]: 'LIST_VIEW',
  [TrackInteractionsEvent.LeaseTransactionConfirmation]: 'LIST_VIEW',
  [TrackInteractionsEvent.LeaseAddressNavigate]: 'LIST_VIEW',
  [TrackInteractionsEvent.CreateLeaseErrorDisplay]: 'LIST_VIEW',
  [TrackInteractionsEvent.LeaseWorkflowLanding]: 'LIST_VIEW',
  [TrackInteractionsEvent.LeaseRepresentationSelect]: 'LIST_VIEW',
  [TrackInteractionsEvent.ArchivedListView]: 'LIST_VIEW',
  [TrackInteractionsEvent.BlurFilterSearchField]: 'LIST_VIEW',
  [TrackInteractionsEvent.ClickGlobalToggle]: 'LIST_VIEW',
  [TrackInteractionsEvent.ClickActionsMenu]: 'LIST_VIEW',
  [TrackInteractionsEvent.ClearFilterAutocomplete]: 'LIST_VIEW',
  [TrackInteractionsEvent.ToggleFilterOption]: 'LIST_VIEW',
  [TrackInteractionsEvent.CloseFiltersDialog]: 'LIST_VIEW',
  [TrackInteractionsEvent.ClickStatusToggleButton]: 'ADDRESS_DECOUPLER',
  [TrackInteractionsEvent.ClickCancelCreateTransaction]: 'ADDRESS_DECOUPLER',
  [TrackInteractionsEvent.ClickCreateTransaction]: 'ADDRESS_DECOUPLER',
  [TrackInteractionsEvent.SelectField]: 'ADDRESS_DECOUPLER',
  [TrackInteractionsEvent.EnterAddressDecouplingModal]: 'ADDRESS_DECOUPLER',
  [TrackInteractionsEvent.ClickMLSAutofill]: 'ADDRESS_DECOUPLER',
  [TrackInteractionsEvent.ClickAddressAutofill]: 'ADDRESS_DECOUPLER',
  [TrackInteractionsEvent.DatePickerAction]: 'DATE_PICKER_ACTION',
  [TrackInteractionsEvent.ReadOnlyIndicatorMount]: 'INTERACTION_LOCKED',
  [TrackInteractionsEvent.ReadOnlyUnlockButtonClick]: 'INTERACTION_LOCKED',
  [TrackInteractionsEvent.UnlockReasonModalMount]: 'INTERACTION_LOCKED',
  [TrackInteractionsEvent.UnlockReasonSelect]: 'INTERACTION_LOCKED',
  [TrackInteractionsEvent.UnlockReasonOtherBlur]: 'INTERACTION_LOCKED',
  [TrackInteractionsEvent.UnlockReasonModalConfirm]: 'INTERACTION_LOCKED',
  [TrackInteractionsEvent.UnlockReasonModalDismiss]: 'INTERACTION_LOCKED',
  [TrackInteractionsEvent.UnlockedBannerMount]: 'INTERACTION_LOCKED',
  [TrackInteractionsEvent.ReLockButtonClick]: 'INTERACTION_LOCKED',
  [TrackInteractionsEvent.ReadOnlyStatusTooltipReveal]: 'LIST_VIEW',
  [TrackInteractionsEvent.DisabledStatusMenuItemTooltipReveal]: 'LIST_VIEW',
};

type EventTypePayloads = Record<TrackInteractionsEvent, EventPayload>;

export const AnalyticsEventsMap: EventTypePayloads = {
  [TrackInteractionsEvent.ClickEditPhotoListItemButton]: {
    eventContainer: 'transactions_list_card',
    eventTarget: 'edit_photo_button',
    eventType: 'click',
    task: 'select',
  },
  [TrackInteractionsEvent.ClickEditPhotoDoneButton]: {
    eventContainer: 'transactions_list_photo_modal',
    eventTarget: 'edit_photo_confirmation_button',
    eventType: 'click',
    task: 'update',
  },
  [TrackInteractionsEvent.UploadInteractionPhoto]: {
    eventContainer: 'transactions_list_photo_modal',
    eventTarget: 'upload_photo',
    eventType: 'drag_and_drop',
    task: 'add',
  },
  [TrackInteractionsEvent.ClickAddTransaction]: {
    eventContainer: 'transactions_list_subheader',
    eventTarget: 'add_transaction_button',
    eventType: 'click',
    task: 'add',
  },
  [TrackInteractionsEvent.ClickNameBubbleClick]: {
    eventContainer: 'transactions_list_card',
    eventTarget: 'agent_avatar_tooltip',
    eventType: 'click',
    task: 'open',
  },
  [TrackInteractionsEvent.LoadMoreTransactions]: {
    eventContainer: 'transactions_list',
    eventTarget: 'infinite_scroll',
    eventType: 'load',
    task: 'view',
  },
  [TrackInteractionsEvent.ChangePage]: {
    eventContainer: 'transactions_list_view',
    eventTarget: 'paginate_button',
    eventType: 'click',
    task: 'navigate',
  },
  [TrackInteractionsEvent.ChangeRowsPerPage]: {
    eventContainer: 'transactions_list_view',
    eventTarget: 'row_page_display',
    eventType: 'click',
    task: 'select',
  },
  [TrackInteractionsEvent.ClickCommunicateWithContact]: {
    eventContainer: 'transactions_list_card',
    eventTarget: 'email_link', // dynamic
    eventType: 'click',
    task: 'open',
  },
  [TrackInteractionsEvent.ClickArchiveUnarchiveTransaction]: {
    eventContainer: 'transactions_list_card',
    eventTarget: 'archive_transaction_button',
    eventType: 'click',
    task: 'archive', // dynamic
  },
  [TrackInteractionsEvent.ClickArchivedTransactionsButton]: {
    eventContainer: 'transactions_list_subheader_dropdown',
    eventTarget: 'archived_transactions_button',
    eventType: 'click',
    task: 'navigate',
  },
  [TrackInteractionsEvent.ClickSortButton]: {
    eventContainer: 'transactions_list_subheader',
    eventTarget: 'sort_button',
    eventType: 'click',
    task: 'expand',
  },
  [TrackInteractionsEvent.ClickSortValue]: {
    eventContainer: 'transactions_list_subheader',
    eventTarget: 'sort_value',
    eventType: 'click',
    task: 'sort',
  },
  [TrackInteractionsEvent.ClickCloneTransactionButton]: {
    eventContainer: 'transactions_list_card',
    eventTarget: 'clone_transaction_button',
    eventType: 'click',
    task: 'clone',
  },
  [TrackInteractionsEvent.ClickFiltersButton]: {
    eventContainer: 'transactions_list_subheader',
    eventTarget: 'filters_toggle_button',
    eventType: 'click',
    task: 'open',
  },
  [TrackInteractionsEvent.ClickApplyFilters]: {
    eventContainer: 'list_view_filter_dropdown',
    eventTarget: 'apply_filters_button',
    eventType: 'click',
    task: 'view',
  },
  [TrackInteractionsEvent.ClickClearAllFilters]: {
    eventContainer: 'list_view_filter_dropdown',
    eventTarget: 'deselect_filters_button',
    eventType: 'click',
    task: 'deselect',
  },
  [TrackInteractionsEvent.ClickChangeTransactionStatus]: {
    eventContainer: 'transactions_list_card',
    eventTarget: 'transaction_status_menuitem',
    eventType: 'click',
    task: 'select',
  },
  [TrackInteractionsEvent.ClickClearSearchButton]: {
    eventContainer: 'transactions_list_subheader',
    eventTarget: 'clear_search_button',
    eventType: 'click',
    task: 'remove',
  },
  [TrackInteractionsEvent.BlurFromSearchField]: {
    eventContainer: 'transactions_list_subheader',
    eventTarget: 'search_field',
    eventType: 'blur',
    task: 'change',
  },
  [TrackInteractionsEvent.ClickTransaction]: {
    eventContainer: 'transactions_list_card',
    eventTarget: 'transaction_photo', // dynamic
    eventType: 'click',
    task: 'navigate',
  },
  [TrackInteractionsEvent.CloneTransactionConfirmation]: {
    eventContainer: '', // dynamic
    eventTarget: 'clone_button',
    eventType: 'click',
    task: 'select',
  },
  [TrackInteractionsEvent.CloneModalMount]: {
    eventContainer: '', // dynamic
    eventTarget: '', // dynamic
    eventType: 'load',
    task: 'view',
  },
  [TrackInteractionsEvent.CloneModalUnmount]: {
    eventContainer: '', // dynamic
    eventTarget: '', // dynamic
    eventType: 'click',
    task: 'exit',
  },
  [TrackInteractionsEvent.CloneModalDisplay]: {
    eventContainer: '', // dynamic
    eventTarget: 'do_not_show_button',
    eventType: 'click',
    task: '', // dynamic
  },
  [TrackInteractionsEvent.LeaseRepresentationNavigate]: {
    eventContainer: 'lease_address_card',
    eventTarget: 'back_to_representation_button',
    eventType: 'click',
    task: 'navigate',
  },
  [TrackInteractionsEvent.LeaseTransactionConfirmation]: {
    eventContainer: 'lease_address_card',
    eventTarget: '', // dynamic
    eventType: 'click',
    task: 'select',
  },
  [TrackInteractionsEvent.LeaseAddressNavigate]: {
    eventContainer: 'lease_representation',
    eventTarget: 'property_address_button',
    eventType: 'click',
    task: 'navigate',
  },
  [TrackInteractionsEvent.CreateLeaseErrorDisplay]: {
    eventContainer: 'lease_address_card',
    eventTarget: 'lease_error_display',
    eventType: 'load',
    task: 'view',
  },
  [TrackInteractionsEvent.LeaseWorkflowLanding]: {
    eventContainer: '', // dynamic
    eventTarget: '', // dynamic
    eventType: 'load',
    task: 'view',
  },
  [TrackInteractionsEvent.LeaseRepresentationSelect]: {
    eventContainer: 'lease_representation',
    eventTarget: '', // dynamic
    eventType: 'click',
    task: 'select',
  },
  [TrackInteractionsEvent.ArchivedListView]: {
    eventContainer: '', // dynamic
    eventTarget: '', // dynamic
    eventType: 'click',
    task: 'navigate',
  },
  [TrackInteractionsEvent.BlurFilterSearchField]: {
    eventContainer: 'transactions_list_subheader',
    eventTarget: 'filter_search_field',
    eventType: 'blur',
    task: 'search',
  },
  [TrackInteractionsEvent.ClickGlobalToggle]: {
    eventContainer: 'transactions_list_subheader', // dynamic
    eventTarget: 'global_view_toggle',
    eventType: 'click',
    task: 'select', // dynamic
  },
  [TrackInteractionsEvent.ClickActionsMenu]: {
    eventContainer: 'transactions_list_subheader',
    eventTarget: 'transactions_list_subheader_dropdown',
    eventType: 'click',
    task: 'expand', // dynamic
  },
  [TrackInteractionsEvent.ClearFilterAutocomplete]: {
    eventContainer: 'filter_container_name', // dynamic
    eventTarget: 'clear_filter_button',
    eventType: 'click',
    task: 'deselect',
  },
  [TrackInteractionsEvent.ToggleFilterOption]: {
    eventContainer: 'filter_container_name', // dynamic
    eventTarget: 'filter_checkbox',
    eventType: 'click',
    task: 'select', // dynamic
  },
  [TrackInteractionsEvent.CloseFiltersDialog]: {
    eventContainer: 'list_view_filter_dropdown',
    eventTarget: 'close_button', // dynamic
    eventType: 'click',
    task: 'exit',
  },
  [TrackInteractionsEvent.ClickStatusToggleButton]: {
    eventContainer: 'listing_address_card',
    eventTarget: 'status_button',
    eventType: 'click',
    task: 'select',
  },
  [TrackInteractionsEvent.ClickCancelCreateTransaction]: {
    eventContainer: 'listing_address_card', // Dynamic
    eventTarget: 'cancel_button',
    eventType: 'click',
    task: 'exit',
  },
  [TrackInteractionsEvent.ClickCreateTransaction]: {
    eventContainer: 'listing_address_card', // Dynamic
    eventTarget: 'create_transaction_button',
    eventType: 'click',
    task: 'add',
  },
  [TrackInteractionsEvent.SelectField]: {
    eventContainer: 'listing_address_card', // Dynamic
    eventTarget: 'location.street1',
    eventType: 'select' as EventPayload['eventType'],
    task: 'update_input',
  },
  [TrackInteractionsEvent.EnterAddressDecouplingModal]: {
    eventContainer: 'listing_address_card', // Dynamic
    eventTarget: 'address_decoupler_view',
    eventType: 'entrance' as EventPayload['eventType'],
    task: 'enter',
  },
  [TrackInteractionsEvent.ClickMLSAutofill]: {
    eventContainer: 'offer_address_card',
    eventTarget: 'mls_autofill_button',
    eventType: 'click',
    task: 'populate',
  },
  [TrackInteractionsEvent.ClickAddressAutofill]: {
    eventContainer: '[interactionType]_address_card', // Dynamic
    eventTarget: 'location.street1',
    eventType: 'click',
    task: 'populate',
  },
  [TrackInteractionsEvent.DatePickerAction]: {
    eventContainer: 'date_picker', // dynamic
    eventTarget: 'calendar_button', // dynamic
    eventType: 'click', // dynamic
    task: 'mount', // dynamic
  },
  [TrackInteractionsEvent.ReadOnlyIndicatorMount]: {
    eventContainer: 'coversheet', // dynamic
    eventTarget: 'read_only_indicator',
    eventType: 'load',
    task: 'view',
  },
  [TrackInteractionsEvent.ReadOnlyUnlockButtonClick]: {
    eventContainer: 'read_only_indicator',
    eventTarget: 'unlock_button',
    eventType: 'click',
    task: 'unlock',
  },
  [TrackInteractionsEvent.UnlockReasonModalMount]: {
    eventContainer: 'unlock_reason_modal',
    eventTarget: 'unlock_reason_modal',
    eventType: 'load',
    task: 'view',
  },
  [TrackInteractionsEvent.UnlockReasonSelect]: {
    eventContainer: 'unlock_reason_modal',
    eventTarget: 'unlock_reason_select',
    eventType: 'click',
    task: 'select',
  },
  [TrackInteractionsEvent.UnlockReasonOtherBlur]: {
    eventContainer: 'unlock_reason_modal',
    eventTarget: 'other_reason_input',
    eventType: 'blur',
    task: 'update_input',
  },
  [TrackInteractionsEvent.UnlockReasonModalConfirm]: {
    eventContainer: 'unlock_reason_modal',
    eventTarget: 'unlock_confirm_button',
    eventType: 'click',
    task: 'unlock',
  },
  [TrackInteractionsEvent.UnlockReasonModalDismiss]: {
    eventContainer: 'unlock_reason_modal',
    eventTarget: 'cancel_button', // dynamic: cancel_button | close_button | background
    eventType: 'click',
    task: 'exit',
  },
  [TrackInteractionsEvent.UnlockedBannerMount]: {
    eventContainer: 'unlocked_banner',
    eventTarget: 'unlocked_banner',
    eventType: 'load',
    task: 'view',
  },
  [TrackInteractionsEvent.ReLockButtonClick]: {
    eventContainer: 'unlocked_banner',
    eventTarget: 're_lock_button',
    eventType: 'click',
    task: 're_lock',
  },
  [TrackInteractionsEvent.ReadOnlyStatusTooltipReveal]: {
    eventContainer: 'transactions_list_card',
    eventTarget: 'status_readonly_tooltip',
    eventType: 'hover',
    task: 'view',
  },
  [TrackInteractionsEvent.DisabledStatusMenuItemTooltipReveal]: {
    eventContainer: 'transactions_list_card',
    eventTarget: 'disabled_status_menuitem_tooltip',
    eventType: 'hover',
    task: 'view',
  },
};
